import React, { useState, useRef, useCallback } from 'react';
import { useLang } from '../../contexts/LangContext.js';
import { useTheme } from '../../contexts/ThemeContext.js';
import { mono } from '../shared/theme.js';
import { PARAM_CATEGORIES,
         importHorizontalPresetCSV, exportHorizontalTemplate } from '../../services/VehiclePresetService.js';

// ============================================================
// VehiclePresetModal.jsx
// EV Powertrain Simulator — 차량 프리셋 CSV 저장/불러오기
// ============================================================

const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
  zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalBox = {
  width: 'min(900px, 96vw)', maxHeight: '88vh', borderRadius: 12,
  overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column',
};

// ── 차량 카드 컴포넌트 ─────────────────────────────────────────
function VehicleCard({ vehicle, isActive, color, onSelect, onRemove, COLORS, e }) {
  const { id, params } = vehicle;
  const displayName  = params.name  || id;
  const shortName    = params.shortName || id.slice(0, 4).toUpperCase();
  const vColor       = params.color || color || COLORS.accent;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8,
      background: isActive ? `${vColor}18` : COLORS.bg,
      border: `2px solid ${isActive ? vColor : COLORS.border}`,
      cursor: 'pointer', transition: 'all 0.15s',
    }}
      onClick={onSelect}
      onMouseOver={(ev) => { if (!isActive) ev.currentTarget.style.borderColor = vColor + '60'; }}
      onMouseOut={(ev)  => { if (!isActive) ev.currentTarget.style.borderColor = COLORS.border; }}
    >
      {/* 색상 도트 + 약칭 */}
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: `${vColor}30`, border: `2px solid ${vColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 900, color: vColor, fontFamily: mono,
      }}>
        {shortName.slice(0, 4)}
      </div>
      {/* 이름 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>
          id: {id}
        </div>
      </div>
      {/* 선택 표시 */}
      {isActive && (
        <div style={{ fontSize: 14, color: vColor, flexShrink: 0 }}>✓</div>
      )}
      {/* 삭제 버튼 */}
      {onRemove && (
        <button
          onClick={(ev) => { ev.stopPropagation(); onRemove(); }}
          style={{ background: 'none', border: 'none', color: COLORS.textDim, fontSize: 16,
            cursor: 'pointer', padding: '2px 4px', borderRadius: 4, flexShrink: 0,
            lineHeight: 1 }}
          title={e('목록에서 제거', 'Remove from list')}
        >✕</button>
      )}
    </div>
  );
}

// ── 메인 모달 ──────────────────────────────────────────────────
function VehiclePresetModal({
  show, onClose,
  paramsA, paramsB,
  vehicleAKey, vehicleBKey,
  setParamsA, setParamsB,
  setVehicleAKey, setVehicleBKey,
  VEHICLE_PRESETS,
  onApply,
}) {
  const COLORS = useTheme();
  const lang   = useLang();
  const e = (ko, en) => lang === 'en' ? en : ko;

  const [importedVehicles, setImportedVehicles] = useState([]);
  const [message, setMessage]     = useState(null);  // {ok, text}
  const [activeTarget, setActiveTarget] = useState('A');  // 'A' | 'B' | 'both'
  const [selectedId, setSelectedId]     = useState(null);
  const hFileInputRef = useRef(null);

  if (!show) return null;

  const showMsg = (ok, text) => {
    setMessage({ ok, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // ── 내장 프리셋 목록 ─────────────────────────────────────────
  const builtinList = VEHICLE_PRESETS
    ? Object.entries(VEHICLE_PRESETS).map(([id, params]) => ({ id, params, builtin: true }))
    : [];

  // ── 불러온 목록 + 내장 합친 전체 목록 ────────────────────────
  const allVehicles = [
    ...importedVehicles.map(v => ({ ...v, builtin: false })),
  ];
  const builtinIds = new Set(builtinList.map(v => v.id));

  // ── 차량 선택 → A/B에 적용 ────────────────────────────────────
  const applyVehicle = (vehicle) => {
    const { id, params } = vehicle;
    const isBuiltin = vehicle.builtin || builtinIds.has(id);

    if (activeTarget === 'A' || activeTarget === 'both') {
      if (isBuiltin && setVehicleAKey) setVehicleAKey(id);
      else setParamsA({ ...params });
    }
    if (activeTarget === 'B' || activeTarget === 'both') {
      if (isBuiltin && setVehicleBKey) setVehicleBKey(id);
      else setParamsB({ ...params });
    }
    setSelectedId(id);
    onApply?.();
    showMsg(true, e(
      `✓ "${params.name || id}" → 차량 ${activeTarget === 'both' ? 'A+B' : activeTarget} 적용`,
      `✓ "${params.name || id}" → applied to Vehicle ${activeTarget === 'both' ? 'A+B' : activeTarget}`
    ));
  };

  // ── 현재 편집 중인 차량 저장 ──────────────────────────────────
  const handleExportCurrent = (target) => {
    const params  = target === 'A' ? paramsA : paramsB;
    const key     = target === 'A' ? vehicleAKey : vehicleBKey;
    const vehicles = [{ id: key || `vehicle_${target.toLowerCase()}`, params }];
    try {
      const fn = exportHorizontalTemplate(vehicles, params.name || key || `vehicle_${target}`);
      showMsg(true, e(`✓ 차량 ${target} 템플릿 CSV 저장 완료`, `✓ Vehicle ${target} template CSV saved`) + ` (${fn})`);
    } catch (err) {
      showMsg(false, e('저장 오류: ', 'Save error: ') + err.message);
    }
  };


  // ── 불러온 목록에서 제거 ──────────────────────────────────────
  const removeImported = (id) => {
    setImportedVehicles(prev => prev.filter(v => v.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ── 가로형 CSV (A열=카테고리, B열=설명, C열=키, D열~=차량) 불러오기 ──
  const handleImportHorizontal = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    ev.target.value = '';
    importHorizontalPresetCSV(file, (result) => {
      if (!result.ok) { showMsg(false, result.message); return; }
      setImportedVehicles(prev => {
        const prevMap = new Map(prev.map(v => [v.id, v]));
        result.vehicles.forEach(v => prevMap.set(v.id, v));
        return Array.from(prevMap.values());
      });
      const warnTxt = result.warnings.length > 0
        ? ` (⚠ ${result.warnings.length}건 경고)` : '';
      showMsg(true, result.message + warnTxt);
    });
  };

  // ── 가로형 템플릿 CSV 내보내기 (내장 프리셋 전체 포함) ──────────
  const handleExportHTemplate = () => {
    try {
      // 내장 프리셋 전체 + 현재 A/B가 내장에 없는 커스텀이면 추가
      const builtinVehicles = builtinList.map(v => ({ id: v.id, params: v.params }));

      // 현재 A/B가 내장에 없는 커스텀 차량이면 맨 앞에 추가
      const builtinIds = new Set(builtinList.map(v => v.id));
      const extras = [];
      if (vehicleAKey && !builtinIds.has(vehicleAKey))
        extras.push({ id: vehicleAKey || 'vehicle_a', params: paramsA });
      if (vehicleBKey && !builtinIds.has(vehicleBKey) && vehicleBKey !== vehicleAKey)
        extras.push({ id: vehicleBKey || 'vehicle_b', params: paramsB });

      const allVehicles = [...extras, ...builtinVehicles];
      const fn = exportHorizontalTemplate(allVehicles, 'ev_presets_full');
      showMsg(true,
        e(`✓ 전체 ${allVehicles.length}개 차량 템플릿 CSV 저장`, `✓ All ${allVehicles.length} vehicles template saved`) +
        ` (${fn})`
      );
    } catch (err) {
      showMsg(false, e('저장 오류: ', 'Save error: ') + err.message);
    }
  };

  // ── 현재 차량 A/B 컬러 ───────────────────────────────────────
  const colorA = paramsA?.color || COLORS.vehA;
  const colorB = paramsB?.color || COLORS.vehB;

  return (
    <div style={modalOverlay} onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      <div style={{ ...modalBox, background: COLORS.panel, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
        onClick={ev => ev.stopPropagation()}>

        {/* ── 헤더 ─────────────────────────────────────────────── */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: `linear-gradient(135deg, ${COLORS.panel}, ${COLORS.bg})`,
          borderRadius: '12px 12px 0 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>🗂️</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                {e('차량 프리셋 관리', 'Vehicle Preset Manager')}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: mono }}>
                {e('CSV 저장/불러오기 · 차량 수 무제한', 'Save/Load CSV · Unlimited vehicles')}
              </div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: COLORS.textDim, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>
            ✕
          </button>
        </div>

        {/* ── 본문 ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* ── 왼쪽: 현재 차량 + 저장 ─────────────────────────── */}
          <div style={{
            width: 'min(260px, 38vw)', borderRight: `1px solid ${COLORS.border}`,
            padding: '14px 12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {/* 현재 차량 */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, fontFamily: mono,
                marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {e('현재 차량', 'Current Vehicles')}
              </div>

              {/* 차량 A */}
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, color: colorA, fontWeight: 700, fontFamily: mono, marginBottom: 3 }}>
                  ● {e('차량 A', 'Vehicle A')}
                </div>
                <div style={{ padding: '8px 10px', borderRadius: 6,
                  background: `${colorA}12`, border: `1px solid ${colorA}40` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{paramsA?.name || vehicleAKey}</div>
                  <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>key: {vehicleAKey}</div>
                </div>
                <button onClick={() => handleExportCurrent('A')}
                  style={{ marginTop: 4, width: '100%', padding: '5px 8px', borderRadius: 5, cursor: 'pointer',
                    background: `${colorA}18`, border: `1px solid ${colorA}50`, color: colorA,
                    fontSize: 10, fontWeight: 700 }}>
                  {e('📋 차량 A 템플릿 CSV 저장', '📋 Save Vehicle A Template CSV')}
                </button>
              </div>

              {/* 차량 B */}
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, color: colorB, fontWeight: 700, fontFamily: mono, marginBottom: 3 }}>
                  ● {e('차량 B', 'Vehicle B')}
                </div>
                <div style={{ padding: '8px 10px', borderRadius: 6,
                  background: `${colorB}12`, border: `1px solid ${colorB}40` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{paramsB?.name || vehicleBKey}</div>
                  <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>key: {vehicleBKey}</div>
                </div>
                <button onClick={() => handleExportCurrent('B')}
                  style={{ marginTop: 4, width: '100%', padding: '5px 8px', borderRadius: 5, cursor: 'pointer',
                    background: `${colorB}18`, border: `1px solid ${colorB}50`, color: colorB,
                    fontSize: 10, fontWeight: 700 }}>
                  {e('📋 차량 B 템플릿 CSV 저장', '📋 Save Vehicle B Template CSV')}
                </button>
              </div>

              {/* A+B 동시 저장 */}
              <button
                onClick={() => {
                  const vehicles = [
                    { id: vehicleAKey || 'vehicle_a', params: paramsA },
                    { id: vehicleBKey || 'vehicle_b', params: paramsB },
                  ];
                  try {
                    const fn = exportHorizontalTemplate(vehicles, 'A_and_B');
                    showMsg(true, e(`✓ A+B 템플릿 CSV 저장 완료`, `✓ A+B template CSV saved`) + ` (${fn})`);
                  } catch (err) {
                    showMsg(false, e('저장 오류: ', 'Save error: ') + err.message);
                  }
                }}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, cursor: 'pointer',
                  background: `linear-gradient(135deg, ${colorA}20, ${colorB}20)`,
                  border: `1px solid ${COLORS.border}`, color: COLORS.text,
                  fontSize: 10, fontWeight: 700 }}>
                {e('📋 A+B 템플릿 CSV 저장', '📋 Save A+B Template CSV')}
              </button>
            </div>

            {/* 내장 프리셋 전체 내보내기 */}
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, fontFamily: mono,
                marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {e('내장 프리셋 내보내기', 'Export Built-in Presets')}
              </div>
              <button onClick={handleExportHTemplate}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, cursor: 'pointer',
                  background: '#22c55e18', border: '1px solid #22c55e50',
                  color: '#22c55e', fontSize: 10, fontWeight: 700 }}>
                {e(`📋 전체 프리셋 템플릿 CSV (${builtinList.length}종)`, `📋 Full Preset Template CSV (${builtinList.length} vehicles)`)}
              </button>
              <div style={{ fontSize: 8.5, color: COLORS.textMuted, marginTop: 5, lineHeight: 1.5 }}>
                {e(
                  'A열=카테고리 · B열=파라미터 설명 · C열=변수명 · D열~=차량값. Excel에서 편집 후 불러오기.',
                  'A=category · B=param description · C=variable name · D~=vehicle values. Edit in Excel then reload.'
                )}
              </div>
            </div>

            {/* 적용 대상 선택 */}
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, fontFamily: mono,
                marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {e('적용 대상', 'Apply Target')}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[
                  { id: 'A',    label: e('차량 A', 'Veh A'), color: colorA },
                  { id: 'B',    label: e('차량 B', 'Veh B'), color: colorB },
                  { id: 'both', label: 'A+B',                color: COLORS.accent },
                ].map(t => (
                  <button key={t.id} onClick={() => setActiveTarget(t.id)}
                    style={{ flex: 1, padding: '5px 4px', borderRadius: 5, cursor: 'pointer',
                      fontSize: 10, fontWeight: 700,
                      background: activeTarget === t.id ? t.color : 'transparent',
                      color: activeTarget === t.id ? '#fff' : COLORS.textDim,
                      border: `1px solid ${activeTarget === t.id ? t.color : COLORS.border}` }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── 오른쪽: 불러오기 + 차량 목록 ───────────────────── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* CSV 불러오기 영역 */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}` }}>

              {/* 가로형 CSV 불러오기 */}
              <div style={{ padding: '10px 12px', borderRadius: '6px',
                  border: `1px solid ${'#22c55e'}50`, background: '#22c55e08' }}>
                  <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 8, lineHeight: 1.6 }}>
                    {e(
                      'A열=카테고리, B열=파라미터 키, C열~=차량 값 형식의 CSV를 불러옵니다. 여러 차량을 한 파일에 담을 수 있습니다.',
                      'Load CSV: col A=category, col B=param key, col C~=vehicle values. Multiple vehicles per file supported.'
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* 불러오기 */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                      borderRadius: 6, cursor: 'pointer',
                      background: 'linear-gradient(135deg, #16a34a, #15803d)',
                      border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                    }}>
                      <span>📂</span>
                      <span>{e('템플릿 CSV 불러오기', 'Load Template CSV')}</span>
                      <input ref={hFileInputRef} type="file" accept=".csv,.txt"
                        onChange={handleImportHorizontal} style={{ display: 'none' }} />
                    </label>
                    {importedVehicles.length > 0 && (
                      <button onClick={() => { setImportedVehicles([]); setSelectedId(null); }}
                        style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                          background: 'transparent', border: `1px solid ${COLORS.border}`,
                          color: COLORS.textMuted, fontSize: 10 }}>
                        {e('목록 초기화', 'Clear list')}
                      </button>
                    )}
                  </div>
                  {/* 포맷 안내 */}
                  <div style={{ marginTop: 8, padding: '5px 8px', borderRadius: 5, fontSize: 8.5,
                    background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                    color: COLORS.textMuted, fontFamily: mono, lineHeight: 1.6 }}>
                    {e(
                      <>## 주석줄 (무시됨)<br/>
                        ,차량 이름,name,"Tesla Model 3","Ioniq 6"<br/>
                        ,차량 약칭,shortName,M3,I6<br/>
                        basic,질량 (Mass),m,1830,1985<br/>
                        battery,DC 링크 전압,V_dc,400,680<br/>
                        motor,전류 PI d축 Kp,Kp_id,1.57,1.54</>,
                      <>## comment lines (ignored)<br/>
                        ,Vehicle Name,name,"Tesla Model 3","Ioniq 6"<br/>
                        ,Short Name,shortName,M3,I6<br/>
                        basic,Mass,m,1830,1985<br/>
                        battery,DC-link Voltage,V_dc,400,680<br/>
                        motor,Current PI d-axis Kp,Kp_id,1.57,1.54</>
                    )}
                  </div>
                </div>

              {/* 메시지 */}
              {message && (
                <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 5, fontSize: 10,
                  background: message.ok ? `${COLORS.accent3}18` : '#ef444418',
                  border: `1px solid ${message.ok ? COLORS.accent3 : '#ef4444'}40`,
                  color: message.ok ? COLORS.accent3 : '#ef4444', fontFamily: mono }}>
                  {message.text}
                </div>
              )}
            </div>

            {/* 차량 목록 — 탭은 VehicleListPane 내부에서 관리 */}
            <VehicleListPane
              importedVehicles={importedVehicles}
              builtinList={builtinList}
              selectedId={selectedId}
              onSelect={applyVehicle}
              onRemove={removeImported}
              COLORS={COLORS}
              e={e}
            />
          </div>
        </div>

        {/* ── 푸터 ─────────────────────────────────────────────── */}
        <div style={{ padding: '10px 20px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>
            {e(
              '💡 가로형 CSV: A열=카테고리 · B열=파라미터키 · C열~=차량 값 (여러 차량 동시 지원)',
              '💡 Horizontal CSV: col A=category · col B=key · col C~=vehicle values (multi-vehicle)'
            )}
          </div>
          <button onClick={onClose}
            style={{ padding: '7px 20px', borderRadius: 6, cursor: 'pointer',
              background: `linear-gradient(135deg, ${COLORS.accent}, #d97706)`,
              border: 'none', color: '#000', fontWeight: 700, fontSize: 12 }}>
            {e('닫기', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 차량 목록 패인 (탭 상태 포함) ────────────────────────────
function VehicleListPane({ importedVehicles, builtinList, selectedId, onSelect, onRemove, COLORS, e }) {
  const [tab, setTab] = React.useState('imported');

  const list = tab === 'imported' ? importedVehicles : builtinList;

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* 탭 */}
      <div style={{ display: 'flex', padding: '8px 16px 0', gap: 4 }}>
        {[
          { id: 'imported', label: e(`불러온 차량 (${importedVehicles.length})`, `Imported (${importedVehicles.length})`), col: COLORS.accent5 },
          { id: 'builtin',  label: e(`내장 프리셋 (${builtinList.length})`, `Built-in (${builtinList.length})`),   col: COLORS.accent3 },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '5px 12px', borderRadius: '5px 5px 0 0', cursor: 'pointer',
              fontSize: 11, fontWeight: 700,
              background: tab === t.id ? t.col : 'transparent',
              color: tab === t.id ? '#fff' : COLORS.textDim,
              border: `1px solid ${tab === t.id ? t.col : COLORS.border}`,
              borderBottom: tab === t.id ? 'none' : `1px solid ${COLORS.border}` }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* 목록 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px' }}>
        {list.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: COLORS.textMuted, fontSize: 11 }}>
            {tab === 'imported'
              ? e('📂 CSV 파일을 불러오면 여기에 차량이 표시됩니다.', '📂 Load a CSV file to see vehicles here.')
              : e('내장 프리셋이 없습니다.', 'No built-in presets.')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 6 }}>
            {list.map(vehicle => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                isActive={selectedId === vehicle.id}
                COLORS={COLORS}
                e={e}
                onSelect={() => onSelect(vehicle)}
                onRemove={tab === 'imported' ? () => onRemove(vehicle.id) : null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { VehiclePresetModal };